import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { BULK_INVITE_WITH_USER_IDS } from "../gql/mutations/event";
import { RECORD_INVITES } from "../gql/mutations/outreach";
import { EVENTS_FOR_MEMBER } from "../gql/queries/eventMatching";
import { SHARE_EVENT_TO_USER_IDS } from "../gql/queries/shareEvent";
import ResponsiveTable from "./ResponsiveTable";

interface EventMatch {
  eventId: string;
  title: string;
  hostName?: string | null;
  hostBadge?: string | null;
  kind?: string | null;
  zone?: string | null;
  startsAt: string;
  localHour?: number | null;
  inHerZone?: boolean | null;
  isFull?: boolean | null;
  spotsLeft?: number | null;
  why?: string | null;
}

interface Props {
  email: string;
  name: string;
  onClose: () => void;
}

/**
 * The events one member would enjoy, with share / invite on each row.
 *
 * Answers "what could I offer *her*" without leaving the page and losing your
 * place in the list — the round trip through Find Events was the reason this
 * never got used mid-triage.
 *
 * Share vs invite is a real difference, not two words for the same thing: a
 * share is a heads-up notification she can ignore, an invite books her a seat.
 * Both are offered because which one is right depends on how well you know her.
 */
const MemberEventsPanel: React.FC<Props> = ({ email, name, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [busyEventId, setBusyEventId] = useState("");

  const { data, loading, error } = useQuery(EVENTS_FOR_MEMBER, {
    variables: { email, days: 21, limit: 25 },
    fetchPolicy: "network-only",
  });

  const [share] = useMutation(SHARE_EVENT_TO_USER_IDS);
  const [recordInvites] = useMutation(RECORD_INVITES);
  const [invite] = useMutation(BULK_INVITE_WITH_USER_IDS);

  const result = data?.adminEventsForMember;
  const member = result?.member;
  const matches: EventMatch[] = result?.matches ?? [];

  const onShare = async (event: EventMatch): Promise<void> => {
    if (!member?.userId) {
      return;
    }
    setBusyEventId(event.eventId);
    try {
      const { data: res } = await share({
        variables: { eventId: event.eventId, userIds: [member.userId], source: "MEMBER_EVENTS" },
      });
      const shared = res?.adminShareEventToUserIds?.sharedCount ?? 0;
      enqueueSnackbar(
        shared > 0 ? `Shared “${event.title}” with ${name}.` : `Couldn't share with ${name}.`,
        { variant: shared > 0 ? "success" : "warning" },
      );
    } catch (caught) {
      enqueueSnackbar(caught instanceof Error ? caught.message : "Share failed.", {
        variant: "error",
      });
    } finally {
      setBusyEventId("");
    }
  };

  const onInvite = async (event: EventMatch): Promise<void> => {
    if (!member?.userId) {
      return;
    }
    if (!window.confirm(`Invite ${name} to “${event.title}”? This books her a place.`)) {
      return;
    }
    setBusyEventId(event.eventId);
    try {
      const { data: res } = await invite({
        variables: { eventId: event.eventId, input: { userIds: [member.userId] } },
      });
      enqueueSnackbar(`Invited ${name} to “${event.title}”.`, { variant: "success" });
      // Write the invite down — inviting from a member's own page left no trace
      // before this, so the board saw her shares and none of her invites.
      try {
        await recordInvites({
          variables: {
            eventId: event.eventId,
            userIds: [member.userId],
            source: "MEMBER_EVENTS",
          },
        });
      } catch {
        /* she is invited either way; the log is a record, not a gate */
      }
      const warnings: string[] = res?.bulkInviteWithUserIds?.warningMessages ?? [];
      if (warnings.length) {
        enqueueSnackbar(warnings.join(" "), { variant: "info" });
      }
    } catch (caught) {
      enqueueSnackbar(caught instanceof Error ? caught.message : "Invite failed.", {
        variant: "error",
      });
    } finally {
      setBusyEventId("");
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Stack spacing={1.5} sx={{ minWidth: 0 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
            🔎 Events for {name}
          </Typography>
          {member?.zone && <Chip size="small" label={member.zone} />}
          {member?.englishBand && (
            <Chip size="small" variant="outlined" label={member.englishBand} />
          )}
          <IconButton size="small" onClick={onClose} aria-label="Close">
            ✕
          </IconButton>
        </Box>

        {loading && <CircularProgress size={20} />}
        {error && <Alert severity="error">Couldn't load her events: {error.message}</Alert>}

        {!loading && !error && matches.length === 0 && (
          <Alert severity="info">
            Nothing upcoming matches her in the next 21 days.
            {result?.hiddenCantJoin
              ? ` ${result.hiddenCantJoin} hidden — she can't access them.`
              : ""}
            {result?.hiddenWrongLevel ? ` ${result.hiddenWrongLevel} hidden — wrong level.` : ""}
            {result?.hiddenPastNewcomerWindow
              ? ` ${result.hiddenPastNewcomerWindow} hidden — she's past her first 3 months, so New Member Welcomes aren't for her.`
              : ""}
          </Alert>
        )}

        {matches.length > 0 && (
          <ResponsiveTable minWidth={640} maxHeight="min(360px, 45vh)">
            <TableHead>
              <TableRow>
                <TableCell>Event</TableCell>
                <TableCell>When</TableCell>
                <TableCell>Why her</TableCell>
                <TableCell align="right">Spots</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {matches.map((event) => (
                <TableRow key={event.eventId} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {event.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {event.hostName ?? "—"}
                      {event.zone ? ` · ${event.zone}` : ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(event.startsAt).toLocaleString(undefined, {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Typography>
                    {/* Her local hour is the thing that decides whether she'd
                        actually come — 2am in her timezone is a no. */}
                    {event.localHour != null && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {event.localHour}:00 her time
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {event.why ?? "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {event.isFull ? (
                      <Chip size="small" color="error" label="Full" />
                    ) : (
                      <Typography variant="caption">{event.spotsLeft ?? "—"}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={busyEventId === event.eventId}
                        onClick={() => onShare(event)}
                      >
                        Share
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={busyEventId === event.eventId || Boolean(event.isFull)}
                        onClick={() => onInvite(event)}
                      >
                        Invite
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ResponsiveTable>
        )}
      </Stack>
    </Paper>
  );
};

export default MemberEventsPanel;
